import './TemplateAgentTip.scss';
import { useEffect, useState } from 'react';
import { MdAutoAwesome, MdClose } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton/IconButton';
import { Appearance, Size } from '@Components/designConstants';
import {
  useDashboardAgentDrawer,
  usePendingAgentPrompt,
  useTemplateTip
} from '../../redux/dashboardAgent';

// Long enough for most charts to have drawn, so the tip doesn't compete with the dashboard loading.
const TIP_DELAY_MS = 3000;

interface TemplateAgentTipProps {
  dashboardId: string;
}

// Shown after a template is instantiated (TemplateGalleryModal sets `templateTip`), so people learn
// the agent can take the dashboard further. Clicking it opens the drawer with the template's starter
// prompt in the box; opening the drawer any other way clears the tip in the reducer, prompt unused.
export const TemplateAgentTip = ({ dashboardId }: TemplateAgentTipProps) => {
  const { setDrawerOpen } = useDashboardAgentDrawer();
  const { templateTip, setTemplateTip } = useTemplateTip();
  const { setPendingPrompt } = usePendingAgentPrompt();
  const tipIsPending = templateTip?.dashboardId === dashboardId;
  // Keyed by dashboard so a later template's tip still waits out its own delay.
  const [delayElapsedFor, setDelayElapsedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!tipIsPending) return undefined;
    const timeout = setTimeout(() => setDelayElapsedFor(dashboardId), TIP_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [tipIsPending, dashboardId]);

  if (!tipIsPending || delayElapsedFor !== dashboardId) return null;

  const dismiss = () => setTemplateTip(null);

  return (
    <div className='template-agent-tip'>
      <Button
        appearance={Appearance.EMPTY}
        leadingElement={<MdAutoAwesome />}
        onClick={() => {
          // Before opening: opening the drawer clears the tip, starter prompt included. Always handed
          // over (empty when there's none) so the drawer knows this chat already got its tip.
          setPendingPrompt(templateTip.starterPrompt ?? '');
          setDrawerOpen(true);
          dismiss();
        }}
      >
        Customize this dashboard with the agent
      </Button>
      <IconButton
        size={Size.SM}
        appearance={Appearance.EMPTY}
        aria-label='Dismiss tip'
        onClick={dismiss}
      >
        <MdClose />
      </IconButton>
    </div>
  );
};
